// Visibility / derived values of the form (updateThreeLabels, updateVisibility, ε automatic).

import { cltOuterForceGrainAngle, currentClt, updateCltVisibility } from "./clt";
import {
  isPanel3,
  isPanelD,
  isPanelMat,
  isSteel3,
  isSteelMat,
  isThreeD,
  isThreeEl,
  isThreeS,
  type EngineCtx,
} from "./ctx";
import { isThetaParallel } from "./grid";
import type { ElementN } from "./materials";

// Notes depending on the connection type (the section titles and the legend come from the page's translations).
export function updateThreeLabels(ctx: EngineCtx): void {
  const t = ctx.v.materiale1;
  const { visible, texts } = ctx.ui;
  const notes = ctx.t.engine.threeNotes;
  const three = isThreeEl(t),
    s3 = isSteel3(t),
    any = three || s3;
  visible.threeNote = any;
  texts.threeNote = any
    ? s3
      ? isPanel3(t)
        ? notes.panelSides
        : notes.steelSides
      : isThreeD(t)
        ? isPanelD(t)
          ? notes.panelDouble
          : notes.double
        : notes.twoSides
    : null;
  visible.notesThree = isThreeS(t);
  visible.notesThreeD = isThreeD(t) && !isPanelD(t);
  visible.notesPanelD = isPanelD(t);
  visible.notesSteel3 = s3 && !isPanel3(t);
  visible.notesPanel3 = isPanel3(t);
  visible.notesPanel = isPanelMat(t) && !isPanelD(t);
  visible.sfalsaDueLatiRow = isThreeS(t) || s3;
}

export function cltWideActive(ctx: EngineCtx, n: ElementN): boolean {
  const v = ctx.v;
  return (
    (n === 1 ? v.mat1Tipo : v.mat2Tipo) === "Pannelli CLT" &&
    (n === 2 || !isSteelMat(v.materiale1)) &&
    (n === 1 ? v.clt1Lato : v.clt2Lato) === "wide"
  );
}
// ε (screw axis / grain angle, only for fh,k): automatic = 90° (the screw is always perpendicular to the in-plane
// grain of both elements). CLT wide: automatic = angle between force and grain of the outer layer (1 decimal).
export function updateEpsVisibility(ctx: EngineCtx, n: ElementN): void {
  const auto = n === 1 ? ctx.v.eps1Auto : ctx.v.eps2Auto;
  ctx.ui.disabled[`eps${n}` as const] = auto;
  const wide = cltWideActive(ctx, n);
  const { epsNote } = ctx.t.engine;
  const refs = ctx.t.elementRefs;
  const orig = n === 1 ? epsNote.element1 : epsNote.element2(isThreeEl(ctx.v.materiale1) ? refs.elements23 : refs.element2);
  ctx.ui.texts[`eps${n}Note` as const] = wide ? epsNote.cltWide : orig;
  if (auto) {
    let val = 90;
    if (wide) {
      try {
        val = Math.round(cltOuterForceGrainAngle(ctx, n, currentClt(ctx, n)) * 10) / 10;
      } catch {
        val = 90;
      }
    }
    ctx.v[`eps${n}` as const] = val;
  }
}

export function updateVisibility(ctx: EngineCtx): void {
  const v = ctx.v,
    { visible, disabled, texts } = ctx.ui;
  const isSteel = isSteelMat(v.materiale1);
  const isPanel = isPanelMat(v.materiale1);
  // CLT narrow side: ε is not used (fh,k = 20·d^-0.5) → field hidden, "automatic" forced. Wide: ε visible.
  const cltEl1 = !isSteel && v.mat1Tipo === "Pannelli CLT" && v.clt1Lato !== "wide";
  const cltEl2 = v.mat2Tipo === "Pannelli CLT" && v.clt2Lato !== "wide";
  if (cltEl1) v.eps1Auto = true;
  if (cltEl2) v.eps2Auto = true;
  visible.eps2Field = !cltEl2;
  updateEpsVisibility(ctx, 2);
  if (isPanel) v.eps1Auto = true; // panel embedment independent of the angle
  visible.eps1Field = !(isSteel || isPanel || cltEl1);
  updateEpsVisibility(ctx, 1);
  visible.rhok1Field = !isSteel;
  visible.rhomean1Row = !(isSteel || isPanel); // K_ser uses ρmean of the timber only (element 2)
  visible.mat1RhoHint = !isSteel;
  visible.steel1DistNote = isSteel;
  visible.spacing1FieldRow = !isSteel;
  visible.steel1SpacingNote = isSteel;
  // Douglas / a4,c,1 reduction are timber exceptions: hidden (and cleared) for a steel plate or a panel
  visible.douglas1Field = !(isSteel || isPanel);
  visible.a4reduce1Field = !(isSteel || isPanel);
  if (isSteel || isPanel) {
    v.douglas1 = false;
    v.a4reduce1 = false;
    texts.a4reduce1Warn = null;
  }
  visible.fi1Field = !(isSteel || isPanel || v.mat1Tipo === "Pannelli CLT");
  visible.panelNote = isPanel && !isSteel3(v.materiale1) && !isThreeD(v.materiale1);
  visible.fi2Field = v.mat2Tipo !== "Pannelli CLT";
  // "Elemento continuo" not selectable at θ = 0°/180° (coincident axes)
  const thetaParallelNow = isThetaParallel(v.thetaNum);
  disabled.continuo1 = thetaParallelNow;
  disabled.continuo2 = thetaParallelNow;
  if (thetaParallelNow) {
    v.continuo1 = false;
    v.continuo2 = false;
  }
  visible.continuo1DisabledNote = thetaParallelNow;
  visible.continuo2DisabledNote = thetaParallelNow;
  visible.d1Field = !v.continuo1;
  visible.continuo1Note = v.continuo1;
  visible.d2Field = !v.continuo2;
  visible.continuo2Note = v.continuo2;
  updateCltVisibility(ctx, 1);
  updateCltVisibility(ctx, 2);
}
